import {
    Braces,
    Check,
    Clipboard,
    Code2,
    Copy,
    FileCode2,
    FileJson,
    FileText,
    Hash,
    Link2,
    XCircle,
} from "lucide-react";
import { useT } from "@magic-md/i18n/react";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { type ContextMenuItem, ContextMenu, useContextMenu } from "../../ContextMenu";
import { cn } from "../../../lib/utils";

export type UnitFooterCopyActionIcon =
    | "body"
    | "config"
    | "document"
    | "id"
    | "output"
    | "path"
    | "reference"
    | "render"
    | "snippet";

export interface UnitFooterCopyAction {
    key: string;
    label: string;
    text?: string;
    getText?: () => string;
    icon?: UnitFooterCopyActionIcon;
    separator?: boolean;
    disabled?: boolean;
}

function renderMenuIcon(icon: UnitFooterCopyActionIcon | undefined): ReactNode {
    switch (icon) {
        case "body":
            return <FileText size={13} />;
        case "config":
            return <Braces size={13} />;
        case "document":
            return <FileCode2 size={13} />;
        case "id":
            return <Hash size={13} />;
        case "output":
            return <FileJson size={13} />;
        case "path":
            return <Link2 size={13} />;
        case "render":
            return <Code2 size={13} />;
        case "snippet":
            return <Clipboard size={13} />;
        case "reference":
        default:
            return <Copy size={13} />;
    }
}

export function stringifyUnitFooterJson(value: unknown): string {
    try {
        const text = JSON.stringify(value, null, 2);
        return text ?? String(value);
    } catch {
        return String(value);
    }
}

async function writeClipboardText(text: string): Promise<boolean> {
    if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
        try {
            await navigator.clipboard.writeText(text);
            return true;
        } catch {
            // Fall through to the DOM fallback below.
        }
    }

    if (typeof document === "undefined" || typeof document.execCommand !== "function") {
        return false;
    }

    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.setAttribute("readonly", "");
    textarea.style.position = "fixed";
    textarea.style.top = "-9999px";
    textarea.style.opacity = "0";

    try {
        document.body.appendChild(textarea);
        textarea.select();
        return document.execCommand("copy");
    } catch {
        return false;
    } finally {
        textarea.remove();
    }
}

function resolveActionText(action: UnitFooterCopyAction): string {
    if (action.getText) return action.getText();
    return action.text ?? "";
}

export const UnitFooterCopyButton = memo(function UnitFooterCopyButton({
    primaryText,
    actions,
    className,
}: {
    primaryText: string;
    actions: UnitFooterCopyAction[];
    className?: string;
}) {
    const t = useT("editor");
    const contextMenu = useContextMenu();
    const resetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const [copyState, setCopyState] = useState<"idle" | "success" | "error">("idle");

    const markCopyState = useCallback((state: "success" | "error") => {
        setCopyState(state);
        if (resetTimerRef.current) {
            clearTimeout(resetTimerRef.current);
        }
        resetTimerRef.current = setTimeout(() => setCopyState("idle"), 1200);
    }, []);

    const copyText = useCallback(
        async (text: string) => {
            const ok = await writeClipboardText(text);
            markCopyState(ok ? "success" : "error");
        },
        [markCopyState],
    );

    useEffect(
        () => () => {
            if (resetTimerRef.current) {
                clearTimeout(resetTimerRef.current);
            }
        },
        [],
    );

    const menuItems = useMemo<ContextMenuItem[]>(
        () =>
            actions.map((action) => ({
                key: action.key,
                label: action.label,
                icon: renderMenuIcon(action.icon),
                separator: action.separator,
                disabled: action.disabled,
                onClick: () => {
                    void copyText(resolveActionText(action));
                },
            })),
        [actions, copyText],
    );

    const title = copyState === "success"
        ? t("common.copied")
        : copyState === "error"
            ? t("copy.failed")
            : t("copy.referenceHint");

    return (
        <>
            <button
                type="button"
                className={cn(
                    "flex h-5 w-5 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-muted hover:text-foreground active:bg-muted",
                    copyState === "success" ? "theme-accent-text" : undefined,
                    copyState === "error" ? "theme-accent-text" : undefined,
                    className,
                )}
                onClick={(event) => {
                    event.stopPropagation();
                    void copyText(primaryText);
                }}
                onContextMenu={contextMenu.handler}
                title={title}
                aria-label={t("copy.reference")}
            >
                {copyState === "success" ? (
                    <Check size={11} />
                ) : copyState === "error" ? (
                    <XCircle size={11} />
                ) : (
                    <Copy size={11} />
                )}
            </button>
            <ContextMenu {...contextMenu.props} items={menuItems} />
        </>
    );
});
